import ChannelComms from './channel_comms_class.js';
import MakedoConfig from './_config.js';
import Fetch from './fetch.js';
import { PubSub } from './websocket.js';
import { MakedoError, ErrorCodes } from './errors.js';

// Helper to dynamically load non-module scripts
const loadScript = (src) => {
    return new Promise((resolve, reject) => {
        // Check if script is already loaded
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }
        
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

// Load protocol.js from same SDK directory as this bundle
const protocolReady = loadScript(MakedoConfig.sdkBase + 'makedo-miniproto.js');

class Bridge {
    #comms = null;         // Private: ChannelComms instance for video server communication
    #pingInterval = null;  // Private: periodic member uts heartbeat while nowinside
    #currentChannelId = null; // Private: channel_id for WS message filtering
    #seenMsgPids = new Set(); // Private: pids already shown via HTTP response; deduplicates WS echo
    #sendInProgress = 0;     // Private: count of in-flight sendMessage HTTP calls
    #msgBuffer = [];         // Private: WS messages held while a send is in flight
    #reconnectTimer = null;   // Private: pending WS reconnect timeout handle
    #reconnectAttempts = 0;   // Private: consecutive reconnect attempts (for backoff)
    #kickHandled = false;     // Private: dedupes kick teardown when both the WS command and the SFU kick arrive
    #pendingDefaultSubscription = null; // Private: default subscription map to apply to each channel's comms on creation

    // BRIDGE
    constructor() {
        this.context=this;  // This is the top level context where default event handling lives.
        this.channelsMap={}; 
        this.dispatcher=null; // WebSocket pub/sub for real-time updates
        this.imageUrlBase = MakedoConfig.imageUrlBase; // Expose config value for image paths
        this.protocolReady = protocolReady; // Promise that resolves when protocol.js is loaded
        this.downstreams = {}; // Cache of downstream streams by member_id-type
        this.selfMemberId = null; // Cache of self member ID
        this.membersMap = {}; // Cache of member media states
        this.startMediaStates = {
            status: "unknown",
            cam_audio_state: null, cam_video_state: null, cam_audio_detail: 'OFF', cam_video_detail: 'OFF',
            screen_audio_state: null, screen_video_state: null, screen_audio_detail: 'OFF', screen_video_detail: 'OFF'
        };
        this.onMemberStateChangeExt = function (member_id, event, streamType) {}; // Callback for member media state changes (member_id, event, streamType)
        this.onKickedExt = (error) => { alert('You have been removed from this meeting.\n\n' + (error?.message || error || '')); }; // Default: alert — override with setOnKicked(). Always receives a MakedoError.
        this.onLivelinessCheckExt = null; // override with setOnLivelinessCheck()
        this.onKickWarningExt = null; // override with setOnKickWarning() — callback(warning): non-destructive advance notice of an upcoming kick
        this.onDbMemberUpdateExt = (() => {console.debug("DB EVENT: memberUpdate no callback set.")});
        this.onDbChannelUpdateExt = (() => {console.debug("DB EVENT: channelUpdate no callback set.")});
        this.onDbInviteUpdateExt = (() => {console.debug("DB EVENT: inviteUpdate no callback set.")});
        this.onInviteResponseExt = (() => {console.debug("DB EVENT: inviteResponse no callback set.")});
        this.onAvailabilityUpdateExt = (() => {console.debug("DB EVENT: availabilityUpdate no callback set.")});
        this.onDbUserUpdateExt = (() => {console.debug("DB EVENT: userUpdate no callback set.")});
        this.onMessageExt = ((msg) => {console.debug("DB EVENT: message received (no callback set):", msg);}); // callback(messageObj, wsIncoming)
        this.onTranscriptionStateExt = ((channelId, state) => {console.debug("TRANSCRIPTION: state change (no callback set):", channelId, state);}); // callback(channelId, state)
        this.onSessionSummaryReadyExt = ((channelId, sessionId) => {console.debug("SESSION: summary ready (no callback set):", channelId, sessionId);}); // callback(channelId, sessionId)
    }

    // 0: BASICS
    hasComms() { return this.#comms !== null; }
    
    // 1: LOGIN/LOGOUT/SELF PROFILE

    /**
     * Try to restore an existing session from the vibelive_auth cookie.
     * If a valid token is found and getSelf() succeeds, returns the user.
     * Does NOT start the WebSocket — caller must call startDispatcher() after
     * deciding whether to accept the restored identity.
     * @returns {Promise<Object|null>} User object if restored, null otherwise
     */
    async restoreSession() {
        console.debug('Bridge: Attempting to restore session from cookie');
        const hasToken = Fetch.restoreAuthToken();
        if (!hasToken) { console.debug('Bridge: No stored auth token found.'); return null; }
        try {
            console.debug('Bridge: Auth token found, validating with getSelf. contextId=', Fetch.contextId, 'authToken=', Fetch.authToken?.slice(0,8)+'...');
            const user = await Fetch.getSelf({ depth: 2 });
            if (user && user.id) {
                console.debug('Bridge: Session restored for user:', user.id);
                return user;
            }
            console.warn('Bridge: getSelf returned no user object:', user);
        } catch (e) {
            const mErr = (e instanceof MakedoError) ? e
                : new MakedoError(ErrorCodes.AUTH_SESSION_EXPIRED, 'Session restore failed: ' + e.message, { cause: e, context: 'restoreSession', retriable: false });
            console.debug('Bridge: Session restore failed.', mErr.code, mErr.message);
        }
        // Token was invalid — clean up
        Fetch.clearAuthToken();
        return null;
    }

    async signupInContext(reqObj) {
        let result= await Fetch.signupInContext(reqObj);
        if (result.status === 'loggedIn') {this.#initDispatcher();}
        return result;
    } // {contextAuthToken, email, password} — contextId auto-injected by Fetch
    async signupAnonInContext(reqObj) {
        let result= await Fetch.signupAnonInContext(reqObj);
        if (result.status === 'loggedIn') {this.#initDispatcher();}
        return result;
    } // {contextAuthToken, password, username} — contextId auto-injected by Fetch
    async authorizeViaProxy(proxyUrl, payload) {
        let result = await Fetch.authorizeViaProxy(proxyUrl, payload);
        if (result.status === 'loggedIn') {this.#initDispatcher();}
        return result;
    } // proxyUrl, {contextId, userId?, username?, minutes?}
    async login(reqObj) {
        let result= await Fetch.login(reqObj);
        if (result.status === 'loggedIn') {this.#initDispatcher();}
        return result;
    }
    async logout(reqObj) {
        let result= await Fetch.logout(reqObj);
        this.resetDispatcher();
        return result;
    }
    /**
     * Disconnect and discard the current WebSocket dispatcher.
     * Call before re-authentication to ensure the next startDispatcher()
     * creates a fresh connection with the correct auth token.
     */
    resetDispatcher() {
        if (this.#reconnectTimer) {
            clearTimeout(this.#reconnectTimer);
            this.#reconnectTimer = null;
        }
        this.#reconnectAttempts = 0;
        if (this.dispatcher) {
            if (this.dispatcher.socket) { this.dispatcher.socket.close(); }
            this.dispatcher = null;
        }
    }
    /**
     * Start the WebSocket dispatcher. Safe to call multiple times — no-ops
     * if a dispatcher already exists. Call AFTER identity is finalized
     * (i.e. after login/signup has set the auth token).
     */
    startDispatcher() {
        return this.#initDispatcher();
    }
    getSelf(reqObj) {return Fetch.getSelf(reqObj);} // {depth = 2} returns user object
    updateSelf(reqObj) {return Fetch.updateSelf(reqObj);} // {user_id, config = {}} returns updated user object
    
    
    // 2: CONTEXT DATA / EVENT CALLBACKS
    getUsers(reqObj) {return Fetch.getUsersByQuery(reqObj);} // {query='', count=10, depth=1} returns array of user objects
    getOneUser(reqObj) {return Fetch.getOneUser(reqObj);} // {user_id, depth = 1} returns user object
    updateUser(reqObj) {return Fetch.updateUser(reqObj);} // {user_id, config = {}} returns updated user object
    setOnUserUpdate(callback) {this.onDbUserUpdateExt=callback;} // callback(user_id, userObj)

    getChannels(reqObj) {return Fetch.getMyChannels(reqObj);} // {count = 3, depth = 1, sortField = 'uts', sortOrder = 'desc'} returns array of channel objects
    getOneChannel(reqObj) {return Fetch.getOneChannel(reqObj);} // {channel_id, room_code, depth = 1} returns channel object
    updateChannel(reqObj) {return Fetch.updateChannel(reqObj);} // {channel_id, config = {}} returns updated channel object
    setChannelMetadata(reqObj) {return Fetch.setChannelMetadata(reqObj);} // {channel_id, metadata} — owner-only app metadata blob
    kickMember(reqObj) {return Fetch.kickMember(reqObj);} // {member_id, message?} — owner-only removal of a member
    setOnChannelUpdate(callback) {this.onDbChannelUpdateExt=callback;} // callback(channel_id, channelObj)
    createNewChannel(reqObj) {return Fetch.createNewChannel(reqObj);} // {title, description, tone, max_size, allows_guests = false, display_name?, transcriptionActive?, …room config} — contextId auto-injected by Fetch
    createQuickChatChannel(reqObj) {return Fetch.createQuickChatChannel(reqObj);} // {invited, message = ''}
    
    getInvites(reqObj) {return Fetch.getOpenActiveInvites(reqObj);} // {count = 10, depth = 1, sortField = 'uts', sortOrder = 'desc'} returns array of invite objects
    setOnInviteUpdate(callback) {this.onDbInviteUpdateExt=callback;} // callback(invite_id, inviteObj)
    sendInvite(reqObj) {return Fetch.sendInvite(reqObj);} // {channel_id, user_id, message?, ttlSeconds?}
    acceptInvite(reqObj) {return Fetch.acceptInvite(reqObj);} // {member_id} returns {channel_id, room_code, ...}
    declineInvite(reqObj) {return Fetch.declineInvite(reqObj);} // {member_id}
    setOnInviteResponse(callback) {this.onInviteResponseExt=callback;} // callback({member_id, channel_id, user_id, status})
    setAvailable(reqObj) {return Fetch.setAvailable(reqObj);} // {open, ttlSeconds, useProfile, seeking} returns {availability}
    getAvailableUsers(reqObj) {return Fetch.getAvailableUsers(reqObj);} // {count} returns array of {user_id, username, availability}
    setOnAvailabilityUpdate(callback) {this.onAvailabilityUpdateExt=callback;} // callback({type:'added'|'removed'|'changed', user_id, username?, availability?})

    getOrCreateSelfMember(reqObj) {return Fetch.getOrCreateSelfMember(reqObj);} // {channel_id, display_name?} returns member object
    getMembers(reqObj) {return Fetch.getMembers(reqObj);} // {channel_id, depth = 1} returns array of member objects
    getOneMember(reqObj) {return Fetch.getOneMember(reqObj);} // {member_id, depth=1} returns member object
    updateMember(reqObj) {return Fetch.updateMember(reqObj);} // {member_id, config = {}} returns updated member object
    setOnMemberUpdate(callback) {this.onDbMemberUpdateExt=callback;} // callback(member_id, memberObj)
    addUserToChannel(reqObj) {return Fetch.addUserToChannel(reqObj);} // {channel_id, user_id}
    addEmailListToChannel(reqObj) {return Fetch.addEmailListToChannel(reqObj);} // {channel_id, email_list}

    getMessages(reqObj) {return Fetch.getMessages(reqObj);}   // {channelId, memberId?, count?, page?, depth?} returns array of message objects

    // sendMessage buffers any WS messages that arrive while the HTTP call is in
    // flight, then registers the returned pid and flushes the buffer. This
    // eliminates the race condition where the server's WS echo arrives before
    // the HTTP response, which would otherwise bypass seenMsgPids dedup.
    async sendMessage(reqObj) {
        this.#sendInProgress++;
        try {
            const result = await Fetch.sendMessage(reqObj);
            // Register the pid BEFORE flushing so the echo is dropped.
            if (result?.pid) this.markMessageSeen(result.pid);
            return result;
        } finally {
            this.#sendInProgress--;
            if (this.#sendInProgress === 0) this.#flushMsgBuffer();
        }
    }

    // Register a message pid as already displayed (from the HTTP sendMessage response).
    // The WS echo of the same message will be silently dropped in #handleInternalMessage.
    markMessageSeen(pid) {
        if (!pid) return;
        this.#seenMsgPids.add(pid);
        // Bound the set to prevent unbounded growth over long sessions.
        if (this.#seenMsgPids.size > 500) this.#seenMsgPids.clear();
    }

    // Flush buffered WS messages once all in-flight sends have completed.
    // Messages whose pid was registered via markMessageSeen are dropped.
    #flushMsgBuffer() {
        const buf = this.#msgBuffer;
        this.#msgBuffer = [];
        for (const {messageObj, wsIncoming} of buf) {
            if (messageObj.pid && this.#seenMsgPids.has(messageObj.pid)) continue;
            try { this.onMessageExt(messageObj, wsIncoming); }
            catch (err) { console.error('Bridge: onMessageExt handler threw:', err); }
        }
    }
    deleteMessage(reqObj) {return Fetch.deleteMessage(reqObj);}          // {messageId}
    deleteTextHistory(reqObj) {return Fetch.deleteTextHistory(reqObj);}  // {channelId, roomMessages?, dmMessages?}
    setOnMessage(callback) {this.onMessageExt=callback;} // callback(messageObj, wsIncoming) — fires on real-time WS message arrival
    setOnTranscriptionState(callback) {this.onTranscriptionStateExt=callback;} // callback(channelId, state) — 'recording'|'uploading'|'processing'|'idle'
    setOnSessionSummaryReady(callback) {this.onSessionSummaryReadyExt=callback;} // callback(channelId, sessionId) — AI summary available via getSessions()

    /**
     * Enumerate active peer connections for the current channel. Used by the optional
     * connection-quality monitor to sample RTCPeerConnection.getStats() each tick.
     * Returns [] when not connected to an SFU.
     * @returns {Array<{direction:'up'|'down', memberId:string, streamType:string, pc:RTCPeerConnection}>}
     */
    getAllActivePeerConnections() {
        return this.#comms ? this.#comms.getAllActivePeerConnections() : [];
    }


    // 3: SERVER / EVENT CALLBACKS
    setupServer(channelId, sfu_url, memberId, accessCode) {
        // Clear channel-specific state from previous channel
        this.#clearPingInterval();
        this.selfMemberId = null;
        this.membersMap = {}; // Clear old channel's member states
        this.downstreams = {}; // Clear old channel's cached streams
        this.#currentChannelId = channelId; // Track for WS message filtering
        this.#kickHandled = false; // New channel — allow the next kick to be processed

        // Create new ChannelComms for this channel
        this.#comms = new ChannelComms(channelId, sfu_url, memberId, accessCode, this); // Pass bridge as context
        this.channelsMap[channelId] = this.#comms;
        // Re-apply any app-set default subscription — comms/streams are recreated per channel.
        if (this.#pendingDefaultSubscription) { this.#comms.setDefaultSubscription(this.#pendingDefaultSubscription); }
        this.selfMemberId = memberId;
        // Immediately register member → session so the server can route member-targeted
        // commands (kicked, livelinessCheck, kickWarning) to this specific session.
        // The 60s ping interval starts later (on join); this covers the PRE-LIVE window.
        this.DBPing('member', memberId);
        // Wire system messages from Galene (e.g. kicked)
        this.#comms.setOnSystemMessageToUser((id, dest, username, time, privileged, kind, error, message) => {
            this.#handleSfuUserMessage(kind, message);
        });
        // Wire recorder lifecycle states (recording/uploading/processing/idle) to the SDK event surface
        this.#comms.setOnTranscriptionState((channelId, state) => {
            this.onTranscriptionStateExt(channelId, state);
        });
        // Setup complete — expects callbacks to be wired by UX layer
    }  
    async joinServer() {
        if (this.#comms) {this.#comms.joinServer();}
    }
    leaveServer(killLocalStreams = true) {
        this.#clearPingInterval();
        if (this.#comms) {this.#comms.leaveServer(killLocalStreams);}
    }
    #clearPingInterval() {
        if (this.#pingInterval !== null) {
            clearInterval(this.#pingInterval);
            this.#pingInterval = null;
            console.debug('Bridge: member ping interval stopped');
        }
    }
    setOnSelfJoined(callback) {
        if (!this.#comms) return;
        this.#comms.setOnSelfJoined((...args) => {
            // Start 60-second heartbeat to keep member.uts current while nowinside
            this.#clearPingInterval();
            this.#pingInterval = setInterval(() => {
                if (this.selfMemberId) {
                    console.debug('Bridge: member ping heartbeat ->', this.selfMemberId);
                    this.DBPing('member', this.selfMemberId);
                }
            }, 60_000);
            console.debug('Bridge: member ping interval started');
            if (callback) callback(...args);
        });
    }
    setOnSelfExited(callback) {
        if (!this.#comms) return;
        this.#comms.setOnSelfExited((...args) => {
            this.#clearPingInterval();
            if (callback) callback(...args);
        });
    }
    setOnSelfLive(callback) {
        if (this.#comms) {this.#comms.setOnSelfLive(callback);}
    }
    
    setOnMemberStateChange(callback) { // expects (member_id, event, streamType) event in {'member_joined', 'member_left', 'member_stream_start', 'member_stream_end', 'member_media_change'}
        this.#setOnNewDownstream(callback); // event='member_stream_start'
        this.#setOnEndDownstream(callback); // event='member_stream_end'
        this.onMemberStateChangeExt=callback;
    }
    setOnKicked(callback) { this.onKickedExt = callback; } // callback(message): called when Galene kicks this user
    setOnLivelinessCheck(callback) { this.onLivelinessCheckExt = callback; } // callback(): called when server sends a liveliness check
    setOnKickWarning(callback) { this.onKickWarningExt = callback; } // callback(warning): advance notice that a kick is coming; non-destructive

    // Private: handle usermessage events from Galene SFU (e.g. kind='kicked').
    // When a kick arrives the WebRTC transports are torn down abruptly by the SFU,
    // so none of the normal onEndDownstream / onSelfExited callbacks will fire.
    // We must synthetically drive the app to a fully-clean state ourselves before
    // surfacing the kick to the caller.  The work is split into three ordered phases:
    //
    //   Phase 1 – Other members: null each cached downstream, set their membersMap
    //             status to 'steppedout', and fire member_stream_end + member_left.
    //             VibeLiveCore reads membersMap[id].status as the preliminary baseline
    //             in #handleMemberLeft before the server fetch, so setting it here
    //             prevents a stale server response from overwriting the correct value.
    //
    //   Phase 2 – Self: zero all media-state flags in membersMap[selfId], mark status
    //             'steppedout', fire member_left for self, then call leaveServer(true)
    //             to stop physical tracks and signal the server.  Note: cam_audio_state
    //             etc. are downstream-only and are always null for self; the _muted
    //             flags are the real target.  VibeLiveCore.#localMedia (the app's
    //             authoritative control state) is cleared separately in setOnSelfExited.
    //
    //   Phase 3 – Notify caller via onKickedExt, deferred with setTimeout(0) so the
    //             browser gets one repaint before any blocking dialog (e.g. alert()).
    #handleSfuUserMessage(kind, message) {
        if (kind === 'kicked') {
            // A LIVE member being kicked can receive the kick twice — once as the app-WS
            // 'kicked' command and once from the SFU when Galene drops them. Run teardown once.
            if (this.#kickHandled) { console.debug('Bridge: duplicate kick signal ignored'); return; }
            this.#kickHandled = true;
            console.debug('Bridge: kicked by server, message:', message);

            // ── Phase 1: tear down all other members' visible state ──────────────
            // downstreams keys are "{memberId}-{streamType}".  Member PIDs can contain
            // dashes so we split on the FIRST dash only (indexOf + substring).
            const seenMembers = new Set();
            for (const key of Object.keys(this.downstreams)) {
                if (this.downstreams[key]) {
                    const dashIdx    = key.indexOf('-');
                    const memberId   = key.substring(0, dashIdx);
                    const streamType = key.substring(dashIdx + 1);

                    this.downstreams[key] = null;

                    if (this.membersMap[memberId]) {
                        this.membersMap[memberId].status = 'steppedout';
                    }
                    this.onMemberStateChangeExt(memberId, 'member_stream_end', streamType);

                    if (!seenMembers.has(memberId)) {
                        seenMembers.add(memberId);
                        this.onMemberStateChangeExt(memberId, 'member_left', null);
                    }
                }
            }

            // ── Phase 2 + 3: clean up self state and stop local tracks ──────────
            // cam_audio_state etc. are downstream-only flags — never populated for self
            // (self's stream is upstream), so they're always null here.  The _muted
            // flags CAN be true if self was muted when kicked; zero them so any caller
            // of getMemberMediaStates(selfId) in the kick→setupServer window gets 'OFF'.
            // NOTE: VibeLiveCore.#localMedia (the authoritative control state) is cleared
            // separately in setOnSelfExited.  setupServer() also wipes membersMap entirely,
            // so none of these values can bleed into the next session.
            const selfState = this.selfMemberId ? this.membersMap[this.selfMemberId] : null;
            if (selfState) {
                selfState.cam_audio_state        = false;
                selfState.cam_video_state        = false;
                selfState.screen_audio_state     = false;
                selfState.screen_video_state     = false;
                selfState.camera_audio_muted     = false;
                selfState.camera_video_muted     = false;
                selfState.screenshare_audio_muted = false;
                selfState.screenshare_video_muted = false;
                selfState.status = 'steppedout';
                this.onMemberStateChangeExt(this.selfMemberId, 'member_left', null);
            }
            this.leaveServer(true); // stops tracks + signals server; may trigger async
                                    // onjoined{kind:'leave'} — status override above wins

            // ── Phase 3: notify the app after a browser repaint ──────────────────
            // The kick message may be a plain display string (current) or a JSON envelope
            // { message, reason, source } once the server encodes structured data. Parse
            // defensively so structured kicks surface reason/source while plain strings still work.
            let displayMsg = message, reason = null, source = null;
            if (typeof message === 'string' && message.trim().startsWith('{')) {
                try {
                    const parsed = JSON.parse(message.trim());
                    if (parsed && typeof parsed === 'object') {
                        // A parsed envelope replaces the raw JSON entirely: a null message
                        // must fall through to the generic text below, not display as JSON.
                        displayMsg = parsed.message ?? null;
                        reason     = parsed.reason ?? null;
                        source     = parsed.source ?? null;
                    }
                } catch (_) { /* not JSON — treat as a plain display string */ }
            }
            const kickError = new MakedoError(ErrorCodes.ROOM_KICKED, displayMsg || 'You were removed from the room by the host', { context: 'handleSfuUserMessage', retriable: false, reason, source });
            setTimeout(() => { this.onKickedExt(kickError); }, 0);

        } else {
            console.debug('Bridge: unhandled SFU user message, kind:', kind, 'message:', message);
        }
    }
    getMemberMediaStates(member_id) { // expects member_id
        //const member_id= reqObj.member_id;
        const recompute=true;
        let memberStates= this.membersMap[member_id];
        if (memberStates && recompute) {
            const camKey=`${member_id}-camera`;
            const screenKey=`${member_id}-screenshare`;
            const camStream= this.downstreams[camKey];
            const screenStream= this.downstreams[screenKey];
            memberStates.cam_audio_detail = this.#getTrackStateDetail(memberStates?.cam_audio_state, camStream, 'audio', 'camera', memberStates);
            memberStates.cam_video_detail = this.#getTrackStateDetail(memberStates?.cam_video_state, camStream, 'video', 'camera', memberStates);
            memberStates.screen_audio_detail = this.#getTrackStateDetail(memberStates?.screen_audio_state, screenStream, 'audio', 'screenshare', memberStates);
            memberStates.screen_video_detail = this.#getTrackStateDetail(memberStates?.screen_video_state, screenStream, 'video', 'screenshare', memberStates);
        }
        return memberStates;
    }
    getMemberMediaStream(memberId, streamType) {
        const key=`${memberId}-${streamType}`;
        return this.downstreams[key];
    }

    // ── Custom labeled upstreams + subscription + discovery (arbitrary-label extension) ──
    async publishStream(label, opts) {
        if (!this.#comms) { console.error('Bridge.publishStream: No comms object available'); return null; }
        return this.#comms.publishStream(label, opts);
    }
    unpublishStream(label) {
        if (this.#comms) { this.#comms.unpublishStream(label); }
        else { console.error('Bridge.unpublishStream: No comms object available'); }
    }
    subscribe(memberId, label, kinds) {
        if (this.#comms) { return this.#comms.subscribeToStream(memberId, label, kinds); }
        console.error('Bridge.subscribe: No comms object available'); return false;
    }
    unsubscribe(memberId, label) {
        if (this.#comms) { return this.#comms.unsubscribeFromStream(memberId, label); }
        console.error('Bridge.unsubscribe: No comms object available'); return false;
    }
    setDefaultSubscription(map) {
        // Persist so it survives channel switches (comms is recreated per channel), then apply now if live.
        this.#pendingDefaultSubscription = map;
        if (this.#comms) { this.#comms.setDefaultSubscription(map); }
    }
    getMemberStreamLabels(memberId) {
        if (this.#comms) { return this.#comms.getMemberStreamLabels(memberId); }
        return [];
    }



    
    // 4: MEDIA local stream management
    // CHANGED API
    //async startMedia(type, audioOn, videoOn, deviceId) {
    async replaceVideoTrack(streamType, deviceId) {
        if (!this.#comms) { console.error('Bridge.replaceVideoTrack: No comms object available'); return; }
        return this.#comms.replaceVideoTrack(streamType, deviceId);
    }
    async replaceAudioTrack(streamType, deviceId) {
        if (!this.#comms) { console.error('Bridge.replaceAudioTrack: No comms object available'); return; }
        return this.#comms.replaceAudioTrack(streamType, deviceId);
    }

    async createLocalMedia(type, audioOn, videoOn, deviceId) {
        if (!this.#comms) {
            console.error("Bridge.createLocalMedia: No comms object available");
            return null;
        }
        
        // Create the new media stream
        const stream = await this.#comms.startMedia(type, audioOn, videoOn, deviceId);
        
        // If stream was created successfully, reapply any existing mute states
        if (stream && this.selfMemberId) {
            const memberStates = this.membersMap[this.selfMemberId];
            if (memberStates) {
                // Check for mute state based on stream type and reapply to tracks
                if (type === 'camera') {
                    // Reapply camera audio mute if it was set
                    if (audioOn && memberStates.camera_audio_muted === true) {
                        const audioTracks = stream.getAudioTracks();
                        if (audioTracks.length > 0) {
                            audioTracks[0].enabled = false;
                            console.debug('Bridge.createLocalMedia: Reapplied camera audio mute state');
                        }
                    }
                    // Reapply camera video mute if it was set
                    if (videoOn && memberStates.camera_video_muted === true) {
                        const videoTracks = stream.getVideoTracks();
                        if (videoTracks.length > 0) {
                            videoTracks[0].enabled = false;
                            console.debug('Bridge.createLocalMedia: Reapplied camera video mute state');
                        }
                    }
                } else if (type === 'screenshare') {
                    // Reapply screenshare audio mute if it was set
                    if (audioOn && memberStates.screenshare_audio_muted === true) {
                        const audioTracks = stream.getAudioTracks();
                        if (audioTracks.length > 0) {
                            audioTracks[0].enabled = false;
                            console.debug('Bridge.createLocalMedia: Reapplied screenshare audio mute state');
                        }
                    }
                    // Reapply screenshare video mute if it was set
                    if (videoOn && memberStates.screenshare_video_muted === true) {
                        const videoTracks = stream.getVideoTracks();
                        if (videoTracks.length > 0) {
                            videoTracks[0].enabled = false;
                            console.debug('Bridge.createLocalMedia: Reapplied screenshare video mute state');
                        }
                    }
                }
            }
        }
        
        return stream;
    }
    // CHANGED API
    //getMedia(type) {
    getLocalStream(type) {
        if (this.#comms) {return this.#comms.getMedia(type);} 
        else {console.error("Bridge.getLocalStream: No comms object available"); return null;} 
    }
    // CHANGED API
    //async stopMedia(streamType, trackType) {
    async stopLocalMedia(streamType, trackType) {
        
        // Clear local mute state for stopped tracks since they no longer exist
        if (this.selfMemberId && this.membersMap[this.selfMemberId]) {
            const memberStates = this.membersMap[this.selfMemberId];
            
            if (!trackType || trackType === null) {
                // Stopping entire stream - clear both audio and video mute states
                if (streamType === 'camera') {
                    memberStates.camera_audio_muted = false;
                    memberStates.camera_video_muted = false;
                } else if (streamType === 'screenshare') {
                    memberStates.screenshare_audio_muted = false;
                    memberStates.screenshare_video_muted = false;
                }
            } else if (trackType === 'audio') {
                // Stopping just audio track
                if (streamType === 'camera') {
                    memberStates.camera_audio_muted = false;
                } else if (streamType === 'screenshare') {
                    memberStates.screenshare_audio_muted = false;
                }
            } else if (trackType === 'video') {
                // Stopping just video track
                if (streamType === 'camera') {
                    memberStates.camera_video_muted = false;
                } else if (streamType === 'screenshare') {
                    memberStates.screenshare_video_muted = false;
                }
            }
        }
        
        // Now actually stop the media
        if (!trackType || trackType==null) {return this.#endFullStreamByType(streamType);} 
        if (trackType === 'audio') {return this.#exitAudioTrack(streamType);}
        if (trackType === 'video') {return this.#exitVideoTrack(streamType);}
    }

    

    // 5: UPSTREAM broadcasting management
    // CHANGED API
    //async startUpstream(streamType, trackType) { 
    async startLiveMedia(streamType, trackType) { 
        return this.#setUpstream(streamType, trackType, true);
    
    }
    // CHANGED API
    //async stopUpstream(streamType, trackType) { 
    async stopLiveMedia(streamType, trackType) { 
        return this.#setUpstream(streamType, trackType, false);
    }
    // CHANGED API
    //async setMutedUpstream(streamType, trackType, isMuted=true) {
    async setMuteLiveMedia(streamType, trackType, isMuted=true) {
        let actOnAudio=false; let actOnVideo=false;
        if (!trackType || trackType==null || trackType==='all' || trackType==='audio') {actOnAudio=true;}
        if (!trackType || trackType==null || trackType==='all' || trackType==='video') {actOnVideo=true;}
        if (actOnAudio) { this.#inChannelSetMutedForUpStream(streamType, 'audio', isMuted);}
        if (actOnVideo) { this.#inChannelSetMutedForUpStream(streamType, 'video', isMuted);}

        // send status change to server, evoking ws events
        const key=streamType+"_"+trackType;
        let config={[key]: (isMuted ? 'muted' : 'unmuted')};
        await this.updateMember({
            member_id: this.selfMemberId,
            config: config
        });

        return;
    }

    // 6: TAGGING
    tagSelf(reqObj) {return Fetch.updateSelfTagInfo(reqObj);} // {add, set, clear} returns nothing
    tagUser(reqObj) {return Fetch.updateUserTagInfo(reqObj);} // {user_id, add, set, clear} returns nothing
    pinUser(reqObj) {return Fetch.pinUser(reqObj);} // {user_id}
    unpinUser(reqObj) {return Fetch.unpinUser(reqObj);} // {user_id}
    tagChannel_TODO(reqObj) {return Fetch.tagChannel(reqObj);} // {channel_id, tag}
    tagUser_OLD_NOT_USED(reqObj) {Fetch.tagUser(reqObj);} // tagUser
    
    // USED INTERNALLY — WS-first with HTTP fallback for fire-and-forget beacons
    setMemberMedia(memberId, mediaType, audioState, videoState) {
        const sent = this.dispatcher?.send({
            type: 'beacon', action: 'setMemberMedia',
            memberId, mediaType, audioState: String(audioState), videoState: String(videoState)
        });
        if (!sent) { Fetch.setMemberMedia(memberId, mediaType, audioState, videoState); }
    }
    DBPing(obj_type, obj_pid) {
        const sent = this.dispatcher?.send({
            type: 'beacon', action: 'ping', obj_type: obj_type, obj_id: obj_pid
        });
        if (!sent) { Fetch.DBPing(obj_type, obj_pid); }
    }
    delog(actor_type, actor_pid, action, notes_obj) {
        const sent = this.dispatcher?.send({
            type: 'beacon', action: 'delog',
            actor_type, actor_pid, action_name: action, notes_obj
        });
        if (!sent) { Fetch.delog(actor_type, actor_pid, action, notes_obj); }
    }
    setMemberStatus(memberId, status) {Fetch.setMemberStatus(memberId, status);} // Always HTTP — must survive page unload
    recordMediaHeartbeat(channelId, memberId, stats) { // WS-first, HTTP fallback
        const sent = this.dispatcher?.send({
            type: 'beacon', action: 'mediaHeartbeat', channelId, memberId, ...stats
        });
        if (!sent) { Fetch.recordBrowserActivity(channelId, 'MEDIA_HEARTBEAT', stats); }
        return !!sent;
    }
    sendLivelinessAck(memberId) {
        this.dispatcher?.send({ type: 'beacon', action: 'livelinessAck', memberId });
    }
    setLatestChannelId_db(newVal) {
        const sent = this.dispatcher?.send({
            type: 'beacon', action: 'setLatestChannelId', newVal
        });
        if (!sent) { Fetch.setLatestChannelId_db(newVal); }
    }
    
    // FUTURE FUNCTIONALITY
    #signup(reqObj) {
        let result= Fetch.signup(reqObj);
        if (result.status === 'loggedIn') {this.#initDispatcher();}
        return result;
    } // {email, password}
    #getIntervalsByUser(reqObj) {return Fetch.getIntervalsByUser(reqObj);} // {user_id, limit=10} returns array of interval objects
    #getIntervalsByTopic(reqObj) {return Fetch.getIntervalsByTopic(reqObj);} // {topic_label, limit=10} returns array of interval objects
    #getIntervalsByChannel(reqObj) {return Fetch.getIntervalsByChannel(reqObj);} // {channel_id, limit=10} returns array of interval objects
    #getMessages(reqObj) {return Fetch.getMessages(reqObj);} // {channelId, memberId = null, count = 10, page = 0, depth = 1}
    #declineInvite(reqObj) {Fetch.declineInvite(reqObj);} // {member_id}
    #sendMessage(reqObj) {Fetch.sendMessage(reqObj);} // {channel_id, member_id = null, content}
    #updateUsername(newVal) {Fetch.updateUsername(newVal);} // newVal
    #updatePublicStatus(newVal) {Fetch.updatePublicStatus(newVal);} // newVal
    #setHomeChannelId(newVal) {Fetch.setHomeChannelId(newVal);} // newVal
    #setLatestAsHomeChannelId() {Fetch.setLatestAsHomeChannelId();}
    #setMemberProfilePic(member_id, newVal) {Fetch.setMemberProfilePic(member_id, newVal);} // member_id, newVal
    #startTranscribing(channel_id) {Fetch.startTranscribing(channel_id);} // channel_id

    // PRIVATE FUNCTIONS
    
    #setOnNewDownstream(callback) {
        // create NewCallback which sets member media state before calling callback
        const newCallback = (streamId, memberId, type, stream) => {
            console.debug(`Bridge: New downstream stream received for memberId: ${memberId}, type: ${type}, streamId: ${streamId}`);
            let hasAudio=false; let hasVideo=false;
            if (stream) {
                this.downstreams[`${memberId}-${type}`] = stream; // Cache the downstream stream for this member and type
                hasAudio=stream.getAudioTracks().length>0;
                hasVideo=stream.getVideoTracks().length>0;
            }
            
            // Ensure member exists in membersMap before setting states
            let states = this.membersMap[memberId];
            if (memberId && !states) {
                states = this.membersMap[memberId] = {...this.startMediaStates};
            }
            
            // Update media states based on stream type
            if (states && (type==="camera")) {states.cam_audio_state=hasAudio; states.cam_video_state=hasVideo;}
            if (states &&  type==="screenshare") {states.screen_audio_state=hasAudio; states.screen_video_state=hasVideo;}

            if (callback) {callback( memberId, 'member_stream_start', type);}
        };
        if (this.#comms) {this.#comms.setOnNewDownstream(newCallback);}
    }
    #setOnEndDownstream(callback) {
        // create NewCallback which sets member media state before calling callback
        const newCallback = (streamId, memberId, type) => {
            console.debug(`Bridge: End downstream stream ended for memberId: ${memberId}, type: ${type}, streamId: ${streamId}`);
            this.downstreams[`${memberId}-${type}`] = null; // Clear downstream cache for this member and type
            let states=null;
            if (memberId) {states=this.membersMap[memberId];}
            if (states && type==="camera") {states.cam_audio_state=false; states.cam_video_state=false;}
            if (states &&  type==="screenshare") {states.screen_audio_state=false; states.screen_video_state=false;}
            if (callback) {callback( memberId, 'member_stream_end', type);}
        };
        if (this.#comms) {this.#comms.setOnEndDownstream(newCallback);}
    }
    /*
    #setOnEndDownTrack(callback) { // expects ()json:{channelId, memberId, streamType, trackType}
        if (this.#comms) {this.#comms.setOnEndDownTrack(callback);}
    }
    */
    async #setUpstream(streamType, trackType, toBeOn) { // never add new local tracks to the stream, just toggle upstream on/off
        let actOnAudio=false; let actOnVideo=false;
        // if no trackType, broadcast current stream as is, otherwise toggle the specified track types
        if (toBeOn && (!trackType || trackType==null)) {return this.#broadcastLocalStream(streamType);} 
        if (trackType==='all' || trackType==='audio') {actOnAudio=true;}
        if (trackType==='all' || trackType==='video') {actOnVideo=true;}
        if (actOnAudio) { this.#inChannelSetStreamAudioTrack(streamType, toBeOn);}
        if (actOnVideo) { this.#inChannelSetStreamVideoTrack(streamType, toBeOn);}
        return;
    }
    #getTrackStateDetail(nowState, stream, trackType, streamType, memberStates) {  
        let trackDetail = 'OFF';  
        if (nowState === true) {
            // First check if we have explicit mute state from WebSocket config
            // This takes precedence since track.enabled doesn't propagate through WebRTC
            if (memberStates) {
                const muteKey = `${streamType}_${trackType}_muted`;
                if (memberStates[muteKey] === true) {
                    return trackType === 'audio' ? 'MUTED' : 'HIDDEN';
                }
            }
            
            // Seems that track is on, get details from stream if available
            if (stream) {
                if (trackType === 'audio') {
                    const audioTracks = stream.getAudioTracks();
                    if (audioTracks.length > 0 && audioTracks[0].readyState === 'live') {
                        trackDetail = audioTracks[0].enabled ? 'ON' : 'MUTED';
                    } else {trackDetail = 'ON (not yet live...)';}
                } else {
                    const videoTracks = stream.getVideoTracks();
                    if (videoTracks.length > 0 && videoTracks[0].readyState === 'live') {
                        trackDetail = videoTracks[0].enabled ? 'ON' : 'HIDDEN';
                    } else {trackDetail = 'ON (not yet live...)';}
                }
            } else {
                trackDetail = 'ON BUT NO STREAM'; // Stream not yet available or removed, but state is on
            }
        } 
        return trackDetail;
    }
    #broadcastLocalStream(type) {
        if (this.#comms) {this.#comms.broadcastLocalStream(type);} 
        else {console.error("Bridge.broadcastLocalStream: No comms object available");}
    }
    #getMyUpstream(type) {
        if (this.#comms) {return this.#comms.getMyUpstream(type);} 
        else {console.error("Bridge.getMyUpstream: No comms object available"); return null;}
    }
    #inChannelSetStreamVideoTrack(type,onOff,deviceId) {
        if (this.#comms) {this.#comms.inChannelSetStreamVideoTrack(type,onOff,deviceId);}
    }
    #inChannelSetStreamAudioTrack(type,onOff,deviceId) {
        if (this.#comms) {this.#comms.inChannelSetStreamAudioTrack(type,onOff,deviceId);}
    }
    async #inChannelSetMutedForUpStream(streamType, trackType, isMuted) {
        if (this.#comms) {this.#comms.inChannelSetMutedForUpStream(streamType, trackType, isMuted);}
    }
    #toggleStreamByType(type, forceVal) {
        if (this.#comms) {return this.#comms.toggleStreamByType(type, forceVal);} 
        else {console.error("Bridge.toggleStreamByType: No comms object available"); return null;}
    }
    #endFullStreamByType(type) {
        if (this.#comms) {this.#comms.endFullStreamByType(type);}
        else {console.error("Bridge.endFullStreamByType: No comms object available");}
    }
    #exitVideoTrack(type) {
        // TODO: update self states for video_state false
        if (this.#comms) {this.#comms.exitVideoTrack(type);}
        else {console.error("Bridge.exitVideoTrack: No comms object available");}
    }
    #exitAudioTrack(type) {
        // TODO: update self states for audio_state false
        if (this.#comms) {this.#comms.exitAudioTrack(type);}
        else {console.error("Bridge.exitAudioTrack: No comms object available");}
    }

    // WebSocket pub/sub: Initialize dispatcher for real-time updates
    #initDispatcher() {
        if (!Fetch.authToken) {
            console.warn('Bridge: Skipping WebSocket init — no auth token. Push notifications will not work.');
            return null;
        }
        console.debug('Bridge: Initializing WebSocket dispatcher');
        if (!this.dispatcher) {
            this.dispatcher = new PubSub();
            this.dispatcher.onError = (err) => {
                if (err?.code === ErrorCodes.NETWORK_WS_DISCONNECTED) {
                    this.#scheduleReconnect();
                } else {
                    this.onMemberStateChangeExt(this.selfMemberId, 'error', null, err);
                }
            };
            // Subscribe to ALL WebSocket commands for internal routing
            this.dispatcher.subscribe('command', (message) => {
                this.#handleInternalCommands(message);
            });
            this.dispatcher.subscribe('member', (message) => {
                this.#handleInternalMember(message);
            });
            this.dispatcher.subscribe('message', (wsIncoming) => {
                this.#handleInternalMessage(wsIncoming);
            });
            
            console.debug('Bridge: WebSocket dispatcher initialized');
        }
        return this.dispatcher;
    }

    // Private: Schedule a WS reconnect with exponential backoff (1s → 2s → 4s … capped at 30s)
    #scheduleReconnect() {
        if (this.#reconnectTimer) return; // already pending
        const delay = Math.min(1000 * Math.pow(2, this.#reconnectAttempts), 30000);
        this.#reconnectAttempts++;
        console.debug(`Bridge: WS disconnected — reconnecting in ${delay}ms (attempt ${this.#reconnectAttempts})`);
        this.#reconnectTimer = setTimeout(() => {
            this.#reconnectTimer = null;
            if (!Fetch.authToken) { this.#reconnectAttempts = 0; return; } // logged out — skip reconnect
            this.resetDispatcher();
            const d = this.#initDispatcher();
            if (d) d.waitForAuth().then(() => { this.#reconnectAttempts = 0; }).catch(() => {});
        }, delay);
    }

    // Private: Route commands between internal handling and client notification
    #handleInternalCommands(wsIncoming) {
        const { command, event } = wsIncoming;

        // Member-targeted commands carry a member_id. If it doesn't match this instance's
        // active member, discard — another instance (the right one) will handle it.
        // This guards against the brief fallback-to-all-sessions window in outToMember.
        const msgMemberId = wsIncoming.member_id;
        if (msgMemberId && this.selfMemberId && msgMemberId !== this.selfMemberId) return;

        // Internal commands - handle here, don't pass to client
        switch(command) {
            // TRANSCRIPTION CODE — server recording baton drives the lazy recorder
            case 'startChannelRecording':
                if (this.#comms) {this.#comms.startRecordingAudio();}
                return; // Don't pass to client

            case 'stopChannelRecording':
                if (this.#comms) {this.#comms.stopRecordingAudio();}
                return; // Don't pass to client

            case 'sessionSummaryReady':
                this.onSessionSummaryReadyExt(wsIncoming.data?.channel_id || this.#currentChannelId, wsIncoming.data?.session_id || wsIncoming.data?.interval_id || null);
                return; // Don't pass to client
            case 'newInvite':
                this.onDbInviteUpdateExt(wsIncoming.data);
                return; 
                
            case 'inviteAccepted':
            case 'inviteDeclined':
            case 'inviteExpired':
                this.onInviteResponseExt({
                    status: command.replace('invite', '').toLowerCase(), // accepted | declined | expired
                    ...(wsIncoming.data || {})
                });
                return;

            case 'availabilityAdded':
            case 'availabilityRemoved':
            case 'availabilityChanged':
                this.onAvailabilityUpdateExt({
                    type: command.replace('availability', '').toLowerCase(), // added | removed | changed
                    ...(wsIncoming.data || {})
                });
                return;

            case 'livelinessCheck':
                this.onLivelinessCheckExt?.();
                return;

            // Server removed this member (owner kick, zombie cleanup). This WS path is the
            // only one that reaches a PRE-LIVE/lobby member — there is no Galene connection
            // to deliver an SFU-level kick. Route into the same teardown as the SFU kick;
            // #handleSfuUserMessage's #kickHandled guard dedupes when a LIVE member gets
            // both this command and the SFU kick.
            case 'kicked': {
                const d = wsIncoming.data || {};
                this.#handleSfuUserMessage('kicked', JSON.stringify({
                    message: d.message ?? null,
                    reason:  d.reason  ?? null,
                    source:  d.source  ?? null,
                }));
                return;
            }

            // Advance notice that a kick is coming (session ending or this member being removed).
            // Non-destructive: nothing is torn down here — the app shows a countdown/banner and
            // the terminal 'kicked' event follows later when the grace period expires.
            case 'kickWarning': {
                const d = wsIncoming.data || {};
                this.onKickWarningExt?.({
                    scope:            d.scope === 'self' ? 'self' : 'room', // 'self' | 'room' (default room)
                    reason:           d.reason ?? null,
                    source:           d.source ?? null,
                    secondsRemaining: (typeof d.seconds_remaining === 'number') ? d.seconds_remaining : null,
                    deadline:         d.deadline ?? null, // epoch ms when the kick will execute, if known
                    message:          d.message ?? null,
                });
                return;
            }

            default:
                console.warn('Bridge: No matching command case, command was:', command);
                return;
                // Not an internal command - pass to client
                //this.dispatcher.publish('client_command', wsIncoming);
        }
    }

    // Private: Route commands between internal handling and client notification
    #handleInternalMember(wsIncoming) {
        const { event, member_id } = wsIncoming;

        // Filter: ignore events for other channels (multi-instance support)
        const msgChannelId = wsIncoming.channel_id || wsIncoming.id_map?.channel;
        if (msgChannelId && this.#currentChannelId && msgChannelId !== this.#currentChannelId) return;

        let thisMember = this.membersMap[member_id];
        if (member_id && !thisMember) {
            thisMember = this.membersMap[member_id] = {...this.startMediaStates};
        }
        
        // Internal member updates - handle here, some don't pass to client
        switch(event) {
                
            case 'member_joined':
                Object.assign(thisMember, wsIncoming.delta);
                this.onMemberStateChangeExt(member_id, event, null);
                return; // Don't pass to client
                
            case 'member_left':
                Object.assign(thisMember, wsIncoming.delta);
                this.onMemberStateChangeExt(member_id, event, null);
                return; // Don't pass to client
            
            // case 'member_stream_start' triggered by on newDownstream
            // case 'member_stream_end' triggered by on endDownstream

            case 'member_camera_change':
                Object.assign(thisMember, wsIncoming.delta);
                this.onMemberStateChangeExt(member_id, 'member_media_change', 'camera');
                return; // Don't pass to client
                
            case 'member_screenshare_change':
                
                // Detect screenshare lifecycle transitions (ON→OFF or OFF→ON)
                // Emit proper stream_start/stream_end events so consumers don't need
                // to interpret media_change events as lifecycle events
                const wasScreenOn = thisMember.screen_video_state === true;
                Object.assign(thisMember, wsIncoming.delta);
                const isScreenOn = thisMember.screen_video_state === true;
                
                if (wasScreenOn && !isScreenOn) {
                    // Screen turned OFF → emit stream end (tile should be removed)
                    console.debug('Bridge: Screenshare OFF→END transition detected');
                    this.onMemberStateChangeExt(member_id, 'member_stream_end', 'screenshare');
                } else if (!wasScreenOn && isScreenOn) {
                    // Screen turned ON → emit stream start (tile should be created)
                    console.debug('Bridge: Screenshare ON→START transition detected');
                    this.onMemberStateChangeExt(member_id, 'member_stream_start', 'screenshare');
                } else {
                    // No lifecycle change, just a property update (e.g. mute)
                    this.onMemberStateChangeExt(member_id, 'member_media_change', 'screenshare');
                }
                return; // Don't pass to client
                
            case 'member_config_update':
                const delta = wsIncoming.delta || {};
                
                // Parse mute state changes and store in membersMap
                let cameraChanged = false;
                let screenshareChanged = false;
                
                if (delta.camera_audio === 'muted') {
                    thisMember.camera_audio_muted = true;
                    cameraChanged = true;
                } else if (delta.camera_audio === 'unmuted') {
                    thisMember.camera_audio_muted = false;
                    cameraChanged = true;
                }
                
                if (delta.camera_video === 'muted') {
                    thisMember.camera_video_muted = true;
                    cameraChanged = true;
                } else if (delta.camera_video === 'unmuted') {
                    thisMember.camera_video_muted = false;
                    cameraChanged = true;
                }
                
                if (delta.screenshare_audio === 'muted') {
                    thisMember.screenshare_audio_muted = true;
                    screenshareChanged = true;
                } else if (delta.screenshare_audio === 'unmuted') {
                    thisMember.screenshare_audio_muted = false;
                    screenshareChanged = true;
                }
                
                if (delta.screenshare_video === 'muted') {
                    thisMember.screenshare_video_muted = true;
                    screenshareChanged = true;
                } else if (delta.screenshare_video === 'unmuted') {
                    thisMember.screenshare_video_muted = false;
                    screenshareChanged = true;
                }
                
                // Notify UI of media changes
                if (cameraChanged) {
                    this.onMemberStateChangeExt(member_id, 'member_media_change', 'camera');
                }
                if (screenshareChanged) {
                    this.onMemberStateChangeExt(member_id, 'member_media_change', 'screenshare');
                }
                
                // Pass to client for any additional UI updates
                this.onDbMemberUpdateExt(wsIncoming);
                return;
                
            default:
                this.onDbMemberUpdateExt(wsIncoming);
                return;
        }
    }

    // Private: Route real-time DB message events to the registered handler
    #handleInternalMessage(wsIncoming) {
        const messageObj = wsIncoming?.data?.object;
        if (!messageObj) {
            console.warn('Bridge: message event with no data.object', wsIncoming);
            return;
        }
        // Channel messages are gated to this instance's active channel so that
        // multi-instance apps don't receive cross-channel messages on the wrong instance.
        // DMs (target_type !== 'channel') always pass through — they may arrive
        // while the user is in a different channel or no channel at all.
        if (messageObj.target_type === 'channel'
                && this.#currentChannelId
                && messageObj.target_id !== this.#currentChannelId) {
            return;
        }
        // If a sendMessage HTTP call is in flight, buffer this WS message so it
        // is processed only after markMessageSeen() has been called with the
        // returned pid. This eliminates the race condition where the WS echo
        // arrives before the HTTP response.
        if (this.#sendInProgress > 0) {
            this.#msgBuffer.push({messageObj, wsIncoming});
            return;
        }
        // Dedup: drop echoes of messages already displayed via the HTTP response.
        if (messageObj.pid && this.#seenMsgPids.has(messageObj.pid)) return;
        try { this.onMessageExt(messageObj, wsIncoming); }
        catch (err) { console.error('Bridge: onMessageExt handler threw:', err); }
    }


}

export default Bridge;

